import { Ng1StateDeclaration } from "@uirouter/angularjs";
import { RecordDataSource } from "../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../records/grids/columns/columnDefinitionService";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import {
  GridConfiguration,
  RecordGridViewService,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
  vsServiceName as RecordGridViewServiceName,
} from "../../records/views/recordGridView";
import { ICompileService, Injectable, IQService } from "angular";
import { $confirm } from "../../../@custom_types/confirm";
import { ColDef } from "../../records/grids/columns/colDef";
import { FilterProvider } from "../../records/grids/columns/filters/FilterProvider";
import { OptionsFilter } from "../../records/grids/columns/filters/subFilters/OptionsFilter";
import { IRowCommandEventArgs } from "../../records/grids/controller";
import { IQuery, ServiceResponse } from "../../records/server/query";
import {
  ConfigurableGridFormViewConfig,
  vsComponentName as configurableGridFormViewComponentName,
} from "../../records/views/configurableGridFormView";
import {
  ColumnDefinitionSetService,
  ColumnDefinitionSetServiceProvider,
  vsServiceName as ColumnDefinitionSetServiceName,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
} from "../../records/grids/columns/columnDefinitionSetService";

export const vsObjectName = "apiKey";

export const vsUserColumns = `${vsObjectName}.user`;
export const vsApiKeyColumns = `${vsObjectName}.apiKey`;

apiKeyConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
];
export function apiKeyConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  configureGrid(
    recordGridViewServiceProvider,
    columnDefinitionSetServiceProvider
  );
}

function configureGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsUserColumns, [
    ColumnDefinitionServiceName,
    (columnDefinitionService: ColumnDefinitionService) => [
      columnDefinitionService.getLinkColDef({
        colId: "info",
        headerName: " ",
        iconClass: "fa fa-info-circle text-primary",
        stateExpression:
          "main.users.detail.apiKeys({ userId: data.UserAuthId })",
      }),
      columnDefinitionService.getStringColDef({
        field: "UserName",
        header: "User",
      }),
      columnDefinitionService.getStringColDef({
        field: "FirstName",
      }),
      columnDefinitionService.getStringColDef({
        field: "LastName",
      }),
      columnDefinitionService.getStringColDef({
        field: "Email",
        hide: true,
      }),
    ],
  ]);

  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsApiKeyColumns, [
    ColumnDefinitionServiceName,
    "$compile",
    (
      columnDefinitionService: ColumnDefinitionService,
      $compile: ICompileService
    ) => [
      columnDefinitionService.getDateTimeColDef({
        field: "ApiKeyCreatedDate",
        header: "Created Date",
      }),
      columnDefinitionService.getDateTimeColDef({
        field: "ApiKeyExpiryDate",
        header: "Expiry Date",
      }),
      columnDefinitionService.getDateTimeColDef({
        field: "ApiKeyCancelledDate",
        header: "Cancelled Date",
      }),
      <ColDef>{
        colId: "cancelApiKey",
        headerName: "Cancel Key",
        field: "Active",
        template: `<button ng-disabled="!data.Active" class="btn btn-danger" ng-click="$emit('command', {data, commandName: 'cancelKey'})">
                  <i class="fa fa-times-circle"></i> Cancel
                </button>`,
        requiresPermission: "adminApiKey",
        suppressSorting: true,
        filter: new FilterProvider("Active", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              queryFieldNames: ["Active"],
              options: [
                { display: "Active", value: true },
                { display: "Inactive", value: false },
              ],
            })
          )
          .getGridFilterFactory(),
      },
    ],
  ]);

  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    ColumnDefinitionSetServiceName,
    (columnDefinitionSetService: ColumnDefinitionSetService) => [
      ...columnDefinitionSetService.getColumnDefinitionSet(vsUserColumns),
      ...columnDefinitionSetService.getColumnDefinitionSet(vsApiKeyColumns),
    ],
  ]);

  addGridConfiguration(recordGridViewServiceProvider, vsObjectName);
  addGridConfiguration(recordGridViewServiceProvider, vsApiKeyColumns);
  addGridConfiguration(recordGridViewServiceProvider, vsUserColumns);
}

function addGridConfiguration(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  name: string
) {
  recordGridViewServiceProvider.addGridConfiguration(name, [
    ServerUtilityName,
    GridSettingsServiceName,
    (serverUtility: ServerUtility, gridSettingsService: GridSettingsService) =>
      getGridConfiguration(serverUtility, gridSettingsService, name),
  ]);
}

function getGridConfiguration(
  serverUtility: ServerUtility,
  gridSettingsService: GridSettingsService,
  name: string
) {
  return {
    defaultSort: "LastName,FirstName,-ApiKeyCreatedDate",
    gridSettings: gridSettingsService.getGridSettings(Symbol(name)),
    nameSingular: "API Key",
    namePlural: "API Keys",
    gridDataSource: new RecordDataSource(serverUtility, "/api/user/apiKeys"),
    columnDefinitionSet: name,
  };
}

export function apiKeyStateDefinition(config?: {
  includeUserColumns?: boolean;
  columnSet?: string;
  defaultQueryResolver?: Injectable<(...args: any[]) => IQuery>;
}): Ng1StateDeclaration {
  const stateDefinition: Ng1StateDeclaration = {
    url: "/apiKey",
    component: configurableGridFormViewComponentName,
    resolve: {
      defaultQuery: config?.defaultQueryResolver ?? [
        () => ({
          Active: true,
        }),
      ],
      gridConfiguration: [
        RecordGridViewServiceName,
        ServerUtilityName,
        "$q",
        "$confirm",
        GridSettingsServiceName,
        (
          recordGridViewService: RecordGridViewService,
          serverUtility: ServerUtility,
          $q: IQService,
          $confirm: $confirm,
          gridSettingsService: GridSettingsService
        ): GridConfiguration => {
          const columnDefinitionSet = config?.columnSet ?? vsObjectName;

          const configuration =
            recordGridViewService.getGridConfiguration(columnDefinitionSet)!;

          configuration.gridSettings = gridSettingsService.getGridSettings(
            Symbol(columnDefinitionSet)
          );

          configuration.onItemCommand = (args: IRowCommandEventArgs<any>) => {
            switch (args.commandName) {
              case "cancelKey":
                return $confirm({
                  text: "Are you sure you want to invalidate this API key?  This will break any existing scripts/programs that depend on this key.",
                }).then(() =>
                  serverUtility
                    .post<ServiceResponse<any[]>>({
                      url: "/api/user/invalidateApiKey",
                      postObject: {
                        UserAuthId: args.data.UserAuthId,
                      },
                    })
                    .then((r) => {
                      args.gridApi.onNewDatasource();
                    })
                );
            }

            return $q.when();
          };

          return configuration;
        },
      ],
      config: [
        "gridConfiguration",
        "defaultQuery",
        (
          gridConfiguration: GridConfiguration,
          defaultQuery: IQuery
        ): ConfigurableGridFormViewConfig => {
          const configurableGridFormViewConfig: ConfigurableGridFormViewConfig =
            {
              gridConfiguration,
              defaultQuery,
              additionalToggleButtons: [
                {
                  id: "activeOnly",
                  text: "Active Only",
                  onChanged(toggleButton, controller) {
                    controller.config.defaultQuery.Active = toggleButton.checked
                      ? true
                      : undefined;
                  },
                  checked: true,
                },
              ],
              showNavButton: false,
              showSelectButtons: false,
            };

          return configurableGridFormViewConfig;
        },
      ],
    },
  };

  return stateDefinition;
}
